import { useEffect, memo } from 'react';

import { ModalRenderer } from '@app/modal';

import AnalyticsService from 'application/services/AnalyticsService';
import { RouteConfig } from 'application/Main/configs/routes';

import RouterProvider, {
  createBrowserRouter,
  Outlet,
  AnonymousRoute,
  PrivateRoute,
  RouterErrorBoundary,
  RedirectRoute,
} from 'application/providers/RouterProvider';

import { useAcceptanceAdvancedErrorNotification } from 'application/hooks';

import { CompanyTagsProvider, OnboardingProvider } from 'application/components/common';
import GeneratedReportsViewport from 'application/components/GeneratedReportsModal/GeneratedReportsViewport';
import MenuItemsProvider from 'application/providers/MenuItemsProvider';
import ModalProvider from 'application/providers/ModalProvider';
import WebPlayerProvider from 'application/providers/WebPlayerProvider';

import { routerConfig } from './configs';
import useActivationFlow from './useActivationFlow';
import useAppHeightCSSVar from './useAppHeightCSSVar';
import useMenusPathnames from './useMenusPathnames';
import useRedirect from './useRedirect';

import './index.scss';

const Application = () => {
  useAppHeightCSSVar();
  useMenusPathnames();
  useActivationFlow();
  useRedirect();
  useAcceptanceAdvancedErrorNotification();

  useEffect(() => {
    AnalyticsService.init();
  }, []);

  return <Outlet />;
};

const Root = memo(() => (
  <OnboardingProvider>
    <ModalProvider>
      <ModalRenderer />
      <CompanyTagsProvider>
        <WebPlayerProvider>
          <GeneratedReportsViewport />
          <MenuItemsProvider>
            <Application />
          </MenuItemsProvider>
        </WebPlayerProvider>
      </CompanyTagsProvider>
    </ModalProvider>
  </OnboardingProvider>
));

const allRoutes: RouteConfig[] = [
  {
    path: '/',
    element: <Root />,
    errorElement: <RouterErrorBoundary />,
    children: [
      {
        element: <RedirectRoute />,
        children: [
          {
            element: <PrivateRoute />,
            children: routerConfig.privateRoutes,
          },
          {
            element: <AnonymousRoute />,
            children: routerConfig.anonymousRoutes,
          },
          ...routerConfig.commonRoutes,
        ],
      },
    ],
  },
];

const router = createBrowserRouter(allRoutes, {
  basename: '/',
  future: {
    v7_relativeSplatPath: true,
  },
});

const ApplicationRouter = () => (
  <RouterProvider
    router={router}
    future={{ v7_startTransition: true }}
  />
);

export default ApplicationRouter;
